import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Button,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogTitle,
  TextField,
} from '@mui/material';
import DivForm from 'src/components/ModalDialogMUI6/styled/DivForm';
import { buildFormToken } from 'src/components/SelectDynamicJoinFormUrl/SelectDynamicJoinFormUrl';
import FormEntityChooser from 'src/components/SelectDynamicJoinFormUrl/FormEntityChooser';
import PersonalizedLinkCheckbox from 'src/components/SelectDynamicJoinFormUrl/PersonalizedLinkCheckbox';
import { STYLE_OPTIONS } from 'src/constants';
import axios from 'src/utils/axios';

const filterOptions = createFilterOptions({
  stringify: ({
    label,
    formCode,
  }) => `${label} ${formCode || ''}`,
});

const isOptionEqualToValue = (option, value) => value?.value && value?.value === option?.value;

// CON-1587 (Bug A) — a Connect Page is stored formCategory=Contact; in Connect
// Page mode show its real type, not "Contact".
const renderOptionAutocomplete = (props, option, cpPageMode = false) => (
  <div
    {...props}
    style={{
      alignItems: 'start',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <b>{option.label}</b>
    {(cpPageMode || option.formCategory) && (
      <span style={{ fontSize: '12px', color: '#5850EC' }}>
        {cpPageMode ? 'Connect Page' : option.formCategory}
      </span>
    )}
  </div>
);

function ModalInsertSmartForm({
  isModalVisible,
  onInsert,
  onCloseModal,
  // CON-1587 — true turns this into a CP Page picker (only CP Pages, no entity
  // chooser, reuses the Contact token family). Default lists Smart Forms and
  // excludes CP Pages.
  cpPageMode = false,
}) {
  const [selectedForm, setSelectedForm] = useState(null);
  const [entityChoice, setEntityChoice] = useState(null);
  const [personalized, setPersonalized] = useState(true);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isModalVisible) return undefined;

    let cancelled = false;
    const fetchForms = async () => {
      setLoading(true);
      try {
        const params = { status: 'Active', pageSize: 5000 };
        if (cpPageMode) params.formType = 'CpPage';
        else params.excludeCpPages = true;
        const resp = await axios.get('/api/smart-forms', { params });
        if (!cancelled) {
          setForms(resp?.data?.forms || []);
        }
      } catch (e) {
        if (!cancelled) setForms([]);
      }
      if (!cancelled) setLoading(false);
    };
    fetchForms();
    return () => { cancelled = true; };
  }, [isModalVisible, cpPageMode]);

  // All Smart Form types are offered. The chat composer is entity-free, so
  // an Event/Group form requires an explicit entity choice (FormEntityChooser
  // below) before Insert — an id-less token would resolve to nothing here.
  const formOptions = forms
    .filter((f) => {
      if ((f.status || 'Active') !== 'Active') return false;
      // CON-1587 — CP Page mode lists only CP Pages; default drops them.
      if (cpPageMode) return f.isCpPage === true;
      if (f.isCpPage) return false;
      return ['Event', 'Group', 'Contact'].includes(f.formCategory);
    })
    .map((f) => ({
      value: f.id,
      label: f.formName || f.formId,
      formCode: f.formId,
      formCategory: f.formCategory,
    }));

  const isEntityFormSelected = selectedForm?.formCategory === 'Event'
    || selectedForm?.formCategory === 'Group';
  const insertBlocked = !selectedForm
    || (isEntityFormSelected && !entityChoice?.entityId);

  // CON-1587 — a CP Page has no fields to skip; its personalized link is about
  // attribution (who opened the page), so the help copy differs.
  const personalizationHelp = cpPageMode ? (
    <>
      When checked, each contact gets a unique link that tells you who
      opened the page. Best for messages sent directly to individual
      contacts.
      <br />
      <br />
      If you&apos;re sending a link that will be shared or forwarded
      (e.g., a host sharing with others), uncheck this.
    </>
  ) : (
    <>
      When checked, each contact receives a unique link
      that recognizes them by name and lets them skip
      fields you already have on file. Best for messages
      sent directly to individual contacts.
      <br />
      <br />
      If you&apos;re sending a link that will be shared
      or forwarded (e.g., a host sharing with party
      guests), uncheck this.
    </>
  );

  const handleClose = () => {
    onCloseModal();
    setSelectedForm(null);
    setEntityChoice(null);
    setPersonalized(true);
  };

  const handleInsert = () => {
    if (insertBlocked) return;
    const token = buildFormToken({
      formCode: selectedForm?.formCode,
      formCategory: selectedForm?.formCategory || 'Contact',
      formEntityId: isEntityFormSelected ? entityChoice?.entityId : null,
      personalized,
    });
    if (token) {
      onInsert(token);
    }
    handleClose();
  };

  return (
    <Dialog
      open={isModalVisible}
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          padding: '24px',
        },
      }}
    >
      <Box
        sx={{
          padding: '0 0 0 16px',
          textAlign: 'center',
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '18px',
            padding: 0,
          }}
        >
          {cpPageMode ? 'Select Connect Page & Insert' : 'Select Smart Form & Insert'}
        </DialogTitle>
      </Box>
      <DivForm>
        <Autocomplete
          options={formOptions}
          value={selectedForm}
          filterOptions={filterOptions}
          renderOption={(props, option) => renderOptionAutocomplete(props, option, cpPageMode)}
          isOptionEqualToValue={isOptionEqualToValue}
          loading={loading}
          onChange={(e, value) => {
            setSelectedForm(value);
            const cat = value?.formCategory;
            setEntityChoice(cat === 'Event' || cat === 'Group'
              ? { mode: 'specific', entityId: null }
              : null);
          }}
          renderInput={(params) => (
            <TextField
              label={cpPageMode ? 'Connect Page' : 'Smart Form'}
              placeholder={cpPageMode ? 'Search or select a Connect Page' : 'Search or select a Smart Form'}
              {...params}
            />
          )}
          handleHomeEndKeys
          selectOnFocus
          clearOnBlur
        />
        {isEntityFormSelected && (
          <FormEntityChooser
            entityKind={selectedForm.formCategory}
            perRecipientAvailable={false}
            ambientEntityId={null}
            value={entityChoice}
            onChange={setEntityChoice}
          />
        )}
        {/* CON-1852 — same warning row as the Insert Smart Form URL picker. */}
        <PersonalizedLinkCheckbox
          checked={personalized}
          onChange={(e) => setPersonalized(e.target.checked)}
          disabled={!selectedForm}
          helpText={personalizationHelp}
          showWarning={personalized}
        />
      </DivForm>
      <DialogActions
        sx={{
          padding: 0,
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            ...STYLE_OPTIONS.BUTTON_PRIMARY_REVERSED,
            minWidth: '105px',
            marginRight: '14px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleInsert}
          disabled={insertBlocked || !selectedForm?.formCode}
          sx={{
            ...STYLE_OPTIONS.BUTTON_PRIMARY,
            minWidth: '105px',
          }}
        >
          Insert
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ModalInsertSmartForm.propTypes = {
  isModalVisible: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onInsert: PropTypes.func.isRequired,
  cpPageMode: PropTypes.bool,
};

export default ModalInsertSmartForm;
